import styled from 'styled-components';
import Link from 'next/link';

import { HeaderNavigationName } from 'layout/header/components/HeaderNavigationName';
import { HoverDropdownMenu } from 'shared/components/HoverDropdownMenu';
import { ICMSBreadcrumb } from 'shared/types/Breadcrumbs';
import { startCase } from 'shared/utils/startCase';
import { trackHeaderBreadcrumbClick } from 'layout/header/utils/trackHeaderBreadcrumbClick';
import { trackHeaderNavClick } from 'layout/header/utils/trackHeaderNavClick';
import { UnorderedList } from 'shared/components/UnorderedList';
import { variableFont, variableFontRegular } from 'styles/theme';

interface IMenuItem {
  dataQa: string;
  href: string;
  label: string;
}

interface IDropdownMenu {
  menuItems: Array<ICMSBreadcrumb>;
  label: string;
  isNewMobileNav?: boolean;
}

interface IHeaderNavigation {
  'data-qa': string;
  menuItems?: Array<ICMSBreadcrumb>;
  href: string;
  label: string;
}

const MenuItem = ({ dataQa, href, label }: IMenuItem) => {
  const handleMenuItemClick = () => {
    trackHeaderNavClick(label);
  };

  return (
    <Link href={href} passHref prefetch={false} legacyBehavior>
      <MenuItem.LabelWrapper onClick={handleMenuItemClick} data-qa={dataQa}>
        {label}
      </MenuItem.LabelWrapper>
    </Link>
  );
};

const DropdownMenu = ({ menuItems, label, isNewMobileNav = false }: IDropdownMenu) => {
  if (!menuItems || menuItems.length === 0) {
    return null;
  }

  const handleBreadcrumbClick = (title, name, slug) => {
    trackHeaderBreadcrumbClick(label, title, name, slug);
  };

  return (
    <DropdownMenu.Wrapper $isNewNavEnabled={isNewMobileNav}>
      <DropdownMenu.FlexWrapper $isNewNavEnabled={isNewMobileNav}>
        {menuItems.map((item) => {
          const { name = '', slug = '', children = [] } = item || {};

          return (
            <DropdownMenu.MenuColumn key={slug} columnCount={menuItems.length}>
              <DropdownMenu.Title>{startCase(name)}</DropdownMenu.Title>
              <DropdownMenu.List>
                {children.length > 0 &&
                  children.map((childItem) => {
                    const { name: childName = '', slug: childSlug = '' } = childItem || {};

                    return (
                      <DropdownMenu.Link key={childSlug}>
                        <Link
                          href={`/${childSlug}`}
                          passHref
                          prefetch={false}
                          data-qa={`header_nav_link_${childName?.toLowerCase()?.replace(' ', '_')}`}
                          tabIndex={0}
                          role="link"
                          onClick={() => handleBreadcrumbClick(name, childName, childSlug)}
                          onKeyDown={() => handleBreadcrumbClick(name, childName, childSlug)}
                          aria-label="dropdown nav link"
                        >
                          <HeaderNavigationName name={childName} />
                        </Link>
                      </DropdownMenu.Link>
                    );
                  })}
              </DropdownMenu.List>
            </DropdownMenu.MenuColumn>
          );
        })}
      </DropdownMenu.FlexWrapper>
    </DropdownMenu.Wrapper>
  );
};

export const HeaderNavigationDesktop = ({
  'data-qa': dataQa,
  menuItems,
  href,
  label,
}: IHeaderNavigation) => (
  <HoverDropdownMenu
    dropdownMenu={menuItems && <DropdownMenu menuItems={menuItems} label={label} />}
    menuItem={<MenuItem dataQa={dataQa} label={label} href={href} />}
  />
);

MenuItem.LabelWrapper = styled.a`
  display: flex;
  padding: 12px 8px;
  ${variableFont({ size: '11.5px', height: '14px', fontWeight: '300' })};
  color: ${({ theme }) => theme.navigation.textColor};
  white-space: nowrap;

  &:hover {
    text-decoration: underline;
    color: ${({ theme }) => theme.navigation.textColor};
  }
`;

DropdownMenu.Wrapper = styled.div<{ $isNewNavEnabled: boolean }>`
  border: 1px solid ${({ theme }) => theme.navigation.borderColor};
  ${({ $isNewNavEnabled }) =>
    $isNewNavEnabled ? `margin-top: 36px; width: calc(100vw - 32px);` : `border-top: none;`}
  padding: 20px 24px;
  background-color: ${({ theme }) => theme.navigation.backgroundColor};

  // adds space above the dropdown menu to keep the dropdown menu open when hovering above
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: -12px;
    height: 12px;
    width: 100%;
  }
`;

DropdownMenu.FlexWrapper = styled.div<{ $isNewNavEnabled: boolean }>`
  display: flex;
  ${({ $isNewNavEnabled }) => $isNewNavEnabled && `flex-direction: column;`}
`;

DropdownMenu.List = styled(UnorderedList)``;

DropdownMenu.MenuColumn = styled.div<{ columnCount?: number }>`
  margin-right: 32px;
  min-width: ${({ columnCount }) => (columnCount === 2 ? '240px' : '150px')};
  text-align: left;
  width: ${({ columnCount }) => (columnCount === 2 ? '240px' : '150px')};

  &:last-child {
    margin-right: 0;
  }
`;

DropdownMenu.Title = styled.h4`
  color: ${({ theme }) => theme.navigation.textColor};
  margin-bottom: 16px;
  cursor: default;
  ${variableFontRegular({ size: '9.5px' })};
  font-weight: 400;
`;

DropdownMenu.Link = styled.li`
  color: ${({ theme }) => theme.navigation.textColor};
  ${variableFontRegular({ size: '11.5px' })};

  > a {
    display: block;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 245px;
    padding-bottom: 8px;

    &:hover {
      text-decoration: underline;
      outline: none;
    }
  }

  &:last-child {
    margin-bottom: 0;
  }
`;
